import { useEffect, useRef } from 'react';

export interface HeroProps {
  badge?: string;
  headline: string;
  accentLine?: string;
  description: string;
  primaryCta?: { text: string; href: string };
  secondaryCta?: { text: string; href: string };
  image: string;
  stats?: Array<{ value: string; label: string }>;
}

const defaultStats = [
  { value: '24/7', label: 'Availability' },
  { value: '100%', label: 'On-Time Rate' },
  { value: '5★', label: 'Rated Service' },
];

export default function PageHero({
  badge = '5-Star Rated · Charleston, SC',
  headline,
  accentLine,
  description,
  primaryCta = { text: 'Book Your Ride Now', href: '#contact' },
  secondaryCta = { text: 'Call for Immediate Pickup', href: 'tel:+18436422440' },
  image,
  stats = defaultStats,
}: HeroProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (el) setTimeout(() => el.classList.add('visible'), 120);
  }, []);

  return (
    <section
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundImage: `url(${image})`, backgroundSize: 'cover', backgroundPosition: 'center top' }}
    >
      {/* Layered overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/60 to-black/85"></div>
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)' }}></div>

      {/* Gold accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-gold to-transparent"></div>

      <div ref={contentRef} className="section-reveal relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
        {badge && (
          <div className="inline-flex items-center gap-2 border border-gold/40 bg-gold/10 backdrop-blur-sm px-5 py-1.5 text-gold text-xs tracking-widest uppercase mb-8">
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-star-fill text-xs"></i></div>
            {badge}
          </div>
        )}

        <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-3">
          {headline}
          {accentLine && (
            <>
              <br />
              <span className="text-gold">{accentLine}</span>
            </>
          )}
        </h1>

        <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed mt-6">
          {description}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={primaryCta.href}
            className="bg-gold hover:bg-gold-light transition-all text-black font-bold px-10 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer"
          >
            {primaryCta.text}
          </a>
          <a
            href={secondaryCta.href}
            className="flex items-center gap-3 border-2 border-white/35 hover:border-gold text-white hover:text-gold transition-all px-10 py-4 text-sm tracking-widest uppercase whitespace-nowrap cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-phone-fill text-sm"></i></div>
            {secondaryCta.text}
          </a>
        </div>

        {stats.length > 0 && (
          <div className="mt-16 grid grid-cols-3 gap-6 max-w-md mx-auto">
            {stats.map((stat, i) => (
              <div key={i} className={`text-center ${i === 1 ? 'border-x border-white/10' : ''}`}>
                <div className="text-gold font-display text-3xl font-bold">{stat.value}</div>
                <div className="text-white/45 text-xs tracking-wide mt-1 uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce opacity-60">
        <div className="w-5 h-8 border border-white/30 rounded-full flex items-start justify-center pt-1.5">
          <div className="w-1 h-2 bg-gold rounded-full"></div>
        </div>
      </div>
    </section>
  );
}
